import { useEffect, useRef, useState } from "react";
import { Navigate, useParams } from "react-router";
import { showNewMessage } from "@intercom/messenger-js-sdk";
import { IconVideo } from "@tabler/icons-react";
import { paths } from "@/App";
import { useDfyOrderQuery } from "@/lib/queries/dfy-queries";
import { useMarkDfyOrderNotificationsSeen } from "@/lib/hooks/notification-hooks";
import { Button } from "@/lib/components/Button/Button";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import EmptyState from "@/lib/components/EmptyState";
import type { DfyDeliveryDto } from "@repo/api/dfy";
import {
  DFY_ORDER_DEADLINE_HOURS,
  getDfyOrderName,
} from "@repo/api/utils/dfy-order";
import { addHours, differenceInMinutes } from "date-fns";
import DfyOrderDetailsLayout, {
  DfyDeliveryCard,
  type DfyOrderDetailsTab,
} from "./DfyOrderDetailsLayout";
import { useSyncDfyOrderAfterSubmit } from "./dfy-order-submitted-hooks";
import { AnalyticsApi } from "@/lib/api/analytics-api";

export default function DfyOrderDetailsPage() {
  const { teamExternalId, orderExternalId } = useParams();

  const { data, isLoading } = useDfyOrderQuery(teamExternalId, orderExternalId);

  const isConfirmingSubmission = useSyncDfyOrderAfterSubmit(
    teamExternalId,
    orderExternalId,
  );

  useMarkDfyOrderNotificationsSeen(orderExternalId);

  const [tab, setTab] = useState<DfyOrderDetailsTab>("deliveries");

  const loggedOrderExternalIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!teamExternalId || !data || isConfirmingSubmission) {
      return;
    }
    const { order, deliveries } = data;
    if (
      order.status === "DRAFT" ||
      loggedOrderExternalIdRef.current === order.externalId
    ) {
      return;
    }
    loggedOrderExternalIdRef.current = order.externalId;
    void AnalyticsApi.logDfyOrderOpened(
      { order, deliveriesCount: deliveries.length },
      teamExternalId,
    );
  }, [teamExternalId, data, isConfirmingSubmission]);

  if (!teamExternalId || !orderExternalId) {
    return null;
  }

  if (isLoading || !data || isConfirmingSubmission) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const { order, submittedByEmail, deliveries, sourceImages, assetMedias } =
    data;

  if (order.status === "DRAFT") {
    return (
      <Navigate
        to={paths.dfyOrderForm(teamExternalId, orderExternalId)}
        replace
      />
    );
  }

  return (
    <DfyOrderDetailsLayout
      teamExternalId={teamExternalId}
      order={order}
      submittedByEmail={submittedByEmail}
      sourceImages={sourceImages}
      assetMedias={assetMedias}
      backTo={paths.dfy(teamExternalId)}
      tab={tab}
      onTabChange={setTab}
      actions={
        <Button
          variant="outline"
          onClick={() =>
            showNewMessage(
              `Hi! I'd like to request changes to "${getDfyOrderName(order.externalId)}".`,
            )
          }
        >
          Request changes
        </Button>
      }
      deliveriesTab={
        <DfyCustomerOrderVideosTab
          teamExternalId={teamExternalId}
          orderExternalId={orderExternalId}
          submittedAt={order.submittedAt}
          deliveries={deliveries}
          onViewSummary={() => setTab("summary")}
        />
      }
    />
  );
}

function DfyCustomerOrderVideosTab({
  teamExternalId,
  orderExternalId,
  submittedAt,
  deliveries,
  onViewSummary,
}: {
  teamExternalId: string;
  orderExternalId: string;
  submittedAt: string | null;
  deliveries: DfyDeliveryDto[];
  onViewSummary: () => void;
}) {
  if (deliveries.length === 0) {
    return (
      <div className="flex min-h-96 flex-1 py-60">
        <EmptyState
          icon={<IconVideo className="h-5 w-5" />}
          header="Order confirmed. We're on it!"
          subHeader={getProductionEstimateText(submittedAt)}
          secondaryActionLabel="View order summary"
          secondaryActionOnClick={onViewSummary}
        />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
      {deliveries.map((delivery) => (
        <DfyDeliveryCard
          key={delivery.externalId}
          delivery={delivery}
          onDownload={() =>
            void AnalyticsApi.logDfyDeliveryDownloaded(
              { orderExternalId, delivery },
              teamExternalId,
            )
          }
        />
      ))}
    </div>
  );
}

function getProductionEstimateText(submittedAt: string | null) {
  const minutesLeft = submittedAt
    ? differenceInMinutes(
        addHours(new Date(submittedAt), DFY_ORDER_DEADLINE_HOURS),
        new Date(),
      )
    : DFY_ORDER_DEADLINE_HOURS * 60;

  if (minutesLeft <= 0) {
    return "Sorry, your videos are taking a bit longer than expected. We'll email you as soon as they're done.";
  }

  const hoursLeft = Math.ceil(minutesLeft / 60);
  const hoursText = `${hoursLeft} ${hoursLeft === 1 ? "hour" : "hours"}`;

  return `Your videos will be ready within ${hoursText}. We'll email you as soon as they're done.`;
}
